import { create } from 'zustand';
import { VALIDATION_WAIT_TIMEOUT_MS } from '@/features/angle/lib/validation-polling';
import {
  isReferenceClassifying,
  type ProjectReference,
} from '@/types/project-reference';

// Fallback poll for reference classification, in case the socket verdict is
// lost. Classification usually takes 15-25 s, so the first request waits.
export const CLASSIFICATION_POLL_INITIAL_DELAY_MS = 20_000;
export const CLASSIFICATION_POLL_INTERVAL_MS = 5_000;
// The server's processing allowance, same as for view validation.
export const CLASSIFICATION_WAIT_BUDGET_MS = VALIDATION_WAIT_TIMEOUT_MS;

/**
 * A reference the poll gave up on: the server row version (`updated_at`) it
 * gave up at, and the text shown instead of the spinner.
 */
export interface StalledReference {
  updatedAt: string;
  message: string;
}

/** Still the same classifying server row the poll gave up on. */
export function isStalledAt(
  reference: ProjectReference,
  entry: StalledReference | undefined
): boolean {
  return (
    entry !== undefined &&
    isReferenceClassifying(reference) &&
    reference.attributes.updated_at === entry.updatedAt
  );
}

export interface ClassificationWatchPlan {
  /** Classifying rows the poll still waits for, sorted (stable effect key). */
  watchIds: string[];
  /** Rows the server has moved on from: settled, or a new version of a given-up row. */
  released: string[];
}

/**
 * Splits server rows into what to poll and what poll state to forget. Pure,
 * so the stop rule (a given-up row is polled again only once the server
 * changes it) is testable without timers.
 */
export function planClassificationWatch(
  references: readonly ProjectReference[],
  stalled: Readonly<Record<string, StalledReference>>,
  waitingSince: Readonly<Record<string, number>>
): ClassificationWatchPlan {
  const watchIds: string[] = [];
  const released: string[] = [];

  for (const reference of references) {
    const entry = stalled[reference.id];
    const stalledNow = isStalledAt(reference, entry);
    if (entry && !stalledNow) {
      released.push(reference.id);
    } else if (
      !isReferenceClassifying(reference) &&
      waitingSince[reference.id] !== undefined
    ) {
      released.push(reference.id);
    }
    if (isReferenceClassifying(reference) && !stalledNow) {
      watchIds.push(reference.id);
    }
  }

  watchIds.sort();
  return { watchIds, released };
}

/** Watched rows whose wait, failed rounds included, has reached the budget. */
export function exhaustedIds(
  ids: readonly string[],
  waitingSince: Readonly<Record<string, number>>,
  now: number
): string[] {
  return ids.filter((id) => {
    const since = waitingSince[id];
    return since !== undefined && now - since >= CLASSIFICATION_WAIT_BUDGET_MS;
  });
}

/** Pause before the next round: the initial delay first, then the interval. */
export function nextPollDelayMs(
  ids: readonly string[],
  waitingSince: Readonly<Record<string, number>>,
  now: number
): number {
  const starts = ids
    .map((id) => waitingSince[id])
    .filter((since): since is number => since !== undefined);
  const elapsed = starts.length > 0 ? now - Math.min(...starts) : 0;
  return Math.max(
    CLASSIFICATION_POLL_INITIAL_DELAY_MS - elapsed,
    CLASSIFICATION_POLL_INTERVAL_MS
  );
}

/**
 * Given-up rows as `failed` carrying the reason, so every tile leaves the
 * spinner through its existing error state. Display-only: a new server
 * version of the row no longer matches and shows the server status again.
 */
export function withStalledReferences(
  references: readonly ProjectReference[],
  stalled: Readonly<Record<string, StalledReference>>
): ProjectReference[] {
  return references.map((reference) => {
    const entry = stalled[reference.id];
    if (!entry || !isStalledAt(reference, entry)) return reference;
    return {
      ...reference,
      attributes: {
        ...reference.attributes,
        classification_status: 'failed',
        status_message: entry.message,
      },
    };
  });
}

interface ClassificationWatchState {
  /** Given-up references by id. */
  stalled: Record<string, StalledReference>;
  /** When the poll started waiting for each classifying reference (ms epoch). */
  waitingSince: Record<string, number>;
  startWaiting: (ids: readonly string[], now: number) => void;
  stall: (entries: Record<string, StalledReference>) => void;
  release: (ids: readonly string[]) => void;
}

/**
 * Shared by every list of the same references (sections, pickers, panels), so
 * the budget is counted once per reference however many screens show it, and
 * reopening a screen does not refill it.
 */
export const useClassificationWatchStore = create<ClassificationWatchState>(
  (set) => ({
    stalled: {},
    waitingSince: {},
    startWaiting: (ids, now) =>
      set((state) => {
        const missing = ids.filter((id) => state.waitingSince[id] === undefined);
        if (missing.length === 0) return state;
        const waitingSince = { ...state.waitingSince };
        for (const id of missing) waitingSince[id] = now;
        return { waitingSince };
      }),
    stall: (entries) =>
      set((state) => ({ stalled: { ...state.stalled, ...entries } })),
    release: (ids) =>
      set((state) => {
        const known = ids.filter(
          (id) =>
            state.stalled[id] !== undefined ||
            state.waitingSince[id] !== undefined
        );
        if (known.length === 0) return state;
        const stalled = { ...state.stalled };
        const waitingSince = { ...state.waitingSince };
        for (const id of known) {
          delete stalled[id];
          delete waitingSince[id];
        }
        return { stalled, waitingSince };
      }),
  })
);
